import type { Route } from "./+types/newsletter-issue";
import { Link } from "react-router";
import { content } from "../lib/content";
import { pageMeta } from "../lib/meta";
import { formatUtcShortDate } from "../utils/formatDate";
import InlineSubscribe from "../components/content/InlineSubscribe/InlineSubscribe";

// One back issue, rendered from the paragraphs that were actually emailed.
//
// Not from a markdown file and not from a second copy of the copy: the Campaign
// row holds the body the send rendered, which is what makes this page evidence
// rather than a claim about what the newsletter is like.
export async function loader({ params }: Route.LoaderArgs) {
  try {
    return { issue: await content.newsletterIssue(params.slug) };
  } catch {
    // A 404 from the API and a network blip are indistinguishable here, and both
    // mean the same thing to the reader: there is nothing at this URL.
    throw new Response("Not Found", { status: 404 });
  }
}

export function meta({ data }: Route.MetaArgs) {
  const issue = data?.issue;
  if (!issue) return pageMeta({ title: "Newsletter | Gazar", description: "", path: "/newsletter" });
  return pageMeta({
    title: `${issue.subject} | Gazar newsletter`,
    // The opening paragraph, which is what the issue is about and is already
    // written. A hand-written summary would be a second thing to keep true.
    description: (issue.body[0] ?? "").slice(0, 200),
    path: `/newsletter/${issue.publicSlug}`,
  });
}

export default function NewsletterIssue({ loaderData }: Route.ComponentProps) {
  const { issue } = loaderData;
  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <p className="mb-3">
        <Link
          to="/newsletter/archive"
          className="text-sm font-semibold uppercase tracking-wider text-brand-navy/60 hover:text-brand-navy"
        >
          Newsletter archive
        </Link>
      </p>
      <h1 className="font-sans text-[40px] sm:text-[52px] font-bold text-brand-navy leading-[1.1] tracking-[-0.022em] mb-3">
        {issue.subject}
      </h1>
      <p className="text-sm text-brand-navy/50 mb-10">Sent {formatUtcShortDate(issue.sentAt)}</p>

      {/* One paragraph per line, which is exactly how the email renders them
          (lib/notify.ts emailHtml). Deliberately not markdown: the email is not
          markdown either, and rendering the archive richer than the thing it is
          an archive OF would make it a different document. */}
      <div className="space-y-5 text-[21px] leading-[1.58] text-brand-navy/90">
        {issue.body.map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>

      <div className="mt-16 border-t border-brand-navy/15 pt-10">
        <InlineSubscribe source={`newsletter-issue:${issue.publicSlug}`} />
      </div>
    </div>
  );
}
